import React, { useEffect, useMemo, useState } from "react";
import { Badge, NoticeState } from "../../ForBrands/ui/BrandUI";
import { SPORTS, GENDERS, TIERS, NCAA_CONFERENCE_GROUPS, buildCerebroCompetitionBody } from "./taxonomy";
import { fetchCerebroCompetitions } from "./services/cacheShareServices";
import { dataStatusNotice, teamsDataStatus } from "./allocationModel";
import { filterTeams, latestEventTitle, latestSeasonOf, optionLabel, spacedSlash, teamDisplayName, twinNoteOf, useCascadeFields } from "./hooks/useCascadeFields";
import { stripSeasonSuffix } from "../../../utils/teamNames";

// The CacheShare cascade: the same seven-step selection CacheShareFilters
// drives (same filter-state contract, same reset semantics), in the V2
// language (bm type scale, 8px inputs, bm palette). A chosen field reads
// orange, an empty optional one neutral.
//
// Two layouts:
//   "sidebar"  stacked fields with hairline dividers (the left rail)
//   "modal"    two-column grid (the Add Player dialog)

const selectCls = (hasValue) =>
  `bm-input bm-select cursor-pointer ${hasValue ? "bm-select--brand !border-bm-orange bg-bm-orange/5" : ""}`;

const StepLabel = ({ step, title, layout }) =>
  layout === "modal" ? (
    <div className="mb-[6px] bm-label-l text-bm-ink">{step ? `Step ${step} · ${title}` : title}</div>
  ) : (
    <div className="mb-1 flex items-baseline gap-2">
      {step && <span className="bm-overline">Step {step}</span>}
      <span className="bm-h-xs text-bm-ink">{title}</span>
    </div>
  );

const Hint = ({ children }) => <p className="bm-body-s text-bm-muted">{children}</p>;

const FieldLabel = ({ children }) => <div className="mb-[6px] bm-label-l text-bm-ink">{children}</div>;

const SelectField = ({ value, onChange, children, placeholder }) => (
  <select value={value ?? ""} onChange={onChange} className={selectCls(value != null && value !== "")}>
    {placeholder != null && <option value="">{placeholder}</option>}
    {children}
  </select>
);

const CascadePicker = ({
  filters,
  onFiltersChange,
  teams,
  selectedTeam,
  onTeamSelect,
  isLoadingTeams,
  layout = "sidebar",
}) => {
  const {
    sport,
    gender,
    tier,
    region,
    body,
    subDivision,
    classDivision,
    conference,
    teamSearch,
  } = filters;

  const grid = layout === "modal";
  const f = useCascadeFields(filters, onFiltersChange);
  const { set, bodyOptions, subDivisionOptions, showRegionStep, showSubDivisionStep, showConferenceStep } = f;

  // "Find any league": search the competition catalogue by name and pick
  // any competition (state associations, showcases, international leagues)
  // that the static taxonomy does not list. Coaches see this picker too
  // (Users page), so its copy never names the data provider.
  const [leagueQuery, setLeagueQuery] = useState("");
  const [leagueHits, setLeagueHits] = useState([]);
  const [leagueSearching, setLeagueSearching] = useState(false);
  useEffect(() => {
    const q = leagueQuery.trim();
    if (q.length < 2) {
      setLeagueHits([]);
      return undefined;
    }
    let cancelled = false;
    setLeagueSearching(true);
    const timer = setTimeout(() => {
      fetchCerebroCompetitions({ q, gender: gender?.value || undefined, limit: 12 })
        .then((rows) => !cancelled && setLeagueHits(rows || []))
        .finally(() => !cancelled && setLeagueSearching(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [leagueQuery, gender]);
  const pickCatalogueLeague = (entry) => {
    setLeagueQuery("");
    setLeagueHits([]);
    set({ body: buildCerebroCompetitionBody(entry), subDivision: null, classDivision: null, conference: null, teamSearch: "" });
  };

  const handleSport = (e) => f.onSport(e.target.value);
  const handleGender = (e) => f.onGender(e.target.value);
  const handleTier = (e) => f.onTier(e.target.value);
  const handleRegion = (e) => f.onRegion(e.target.value);
  const handleBody = (e) => f.onBody(e.target.value);
  const handleSubDivision = (e) => f.onSubDivision(e.target.value);
  const handleClassDivision = (e) => f.onClassDivision(e.target.value);
  const handleConference = (e) => f.onConference(e.target.value);
  const handleTeamSearch = (e) => f.onTeamSearch(e.target.value);

  const filteredTeams = useMemo(() => filterTeams(teams, teamSearch), [teams, teamSearch]);

  const scopeName =
    conference?.name || subDivision?.name || body?.name ||
    region?.name || tier?.name || "";
  const teamCountLabel = `${filteredTeams.length} ${
    filteredTeams.length === 1 ? "team" : "teams"
  }${scopeName ? ` in ${scopeName}` : ""}${filteredTeams.length > 100 ? " · showing the first 100, type to narrow" : ""}`;

  const { genderReady } = f;

  // Sidebar: each step is a section separated by hairlines (per Figma).
  // Modal: two-column grid, no dividers.
  const wrap = grid
    ? "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5"
    : "flex flex-col divide-y divide-bm-mutedbg";
  const sec = grid ? "" : "py-5 first:pt-0 last:pb-0";

  return (
    <div>
      <div className={wrap}>
        {/* Step 1 · Sport (+ Gender in the same section) */}
        <div className={sec}>
          <StepLabel step={1} title="Sport" layout={layout} />
          <div className={grid ? "" : "mt-2"}>
            {!grid && <FieldLabel>Sport</FieldLabel>}
            <SelectField
              value={sport?.value}
              onChange={handleSport}
              placeholder="Pick a sport…"
            >
              {SPORTS.map((s) => (
                <option key={s.value} value={s.value} disabled={s.status !== "live"}>
                  {optionLabel(s)}
                </option>
              ))}
            </SelectField>
          </div>
          {!grid && sport?.hasGender && (
            <div className="mt-3.5">
              <FieldLabel>Gender</FieldLabel>
              <SelectField
                value={gender?.value}
                onChange={handleGender}
                placeholder="Pick a gender…"
              >
                {GENDERS.map((g) => (
                  <option key={g.value} value={g.value}>{spacedSlash(g.name)}</option>
                ))}
              </SelectField>
            </div>
          )}
        </div>

        {/* Gender gets its own grid cell in the modal layout */}
        {grid && sport?.hasGender && (
          <div>
            <StepLabel title="Gender" layout={layout} />
            <SelectField
              value={gender?.value}
              onChange={handleGender}
              placeholder="Pick a gender…"
            >
              {GENDERS.map((g) => (
                <option key={g.value} value={g.value}>{spacedSlash(g.name)}</option>
              ))}
            </SelectField>
          </div>
        )}

        {/* Step 2 · Tier */}
        {genderReady && (
          <div className={sec}>
            <StepLabel step={2} title="Tier" layout={layout} />
            {!grid && <Hint>Selecting a new tier resets the filters below.</Hint>}
            <div className={grid ? "" : "mt-2.5"}>
              <SelectField
                value={tier?.value}
                onChange={handleTier}
                placeholder="Pick a tier…"
              >
                {TIERS.map((t) => (
                  <option key={t.value} value={t.value}>{t.name}</option>
                ))}
              </SelectField>
            </div>
            {!grid && tier && !showRegionStep && tier.value === "college" && (
              <div className="mt-2.5">
                <Hint>Step 3 · Region skipped for College.</Hint>
              </div>
            )}
          </div>
        )}

        {/* Step 3 · Region (Pro + HS only) */}
        {genderReady && tier && showRegionStep && (
          <div className={sec}>
            <StepLabel step={3} title="Region" layout={layout} />
            <div className={grid ? "" : "mt-2"}>
              <SelectField
                value={region?.value}
                onChange={handleRegion}
                placeholder="All regions"
              >
                {f.regionOptions.map((r) => (
                  <option key={r.value} value={r.value}>{r.name}</option>
                ))}
              </SelectField>
            </div>
          </div>
        )}

        {/* Step 4 · Governing Body / League */}
        {genderReady && tier && (
          <div className={sec}>
            <StepLabel
              step={4}
              title={tier?.value === "college" ? "Governing Body" : "League"}
              layout={layout}
            />
            {!grid && (
              <Hint>
                {tier?.value === "college"
                  ? "Required. Unlocks the team list and player search."
                  : "Optional. Skip to see all teams in this tier."}
              </Hint>
            )}
            <div className="relative mb-2.5">
                <input
                  type="text"
                  value={leagueQuery}
                  onChange={(e) => setLeagueQuery(e.target.value)}
                  placeholder="Search every league and event (state association, showcase, circuit)"
                  className="bm-input"
                  aria-label="Find any league"
                />
                {(leagueHits.length > 0 || leagueSearching) && leagueQuery.trim().length >= 2 && (
                  <ul className="absolute left-0 right-0 z-30 mt-1 max-h-[260px] overflow-y-auto rounded-[8px] border border-bm-border bg-white shadow-lg">
                    {leagueSearching && leagueHits.length === 0 && (
                      <li className="px-4 py-2 bm-body-s text-bm-muted">Searching leagues...</li>
                    )}
                    {leagueHits.map((h) => (
                      <li key={h.key}>
                        <button
                          type="button"
                          onClick={() => pickCatalogueLeague(h)}
                          className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-bm-subtle"
                        >
                          <span className="min-w-0 truncate bm-body-m text-bm-ink">{h.label}</span>
                          <span className="shrink-0 bm-body-s text-bm-muted">
                            {[h.level, h.gender === "F" ? "Girls / Women" : h.gender === "M" ? "Boys / Men" : ""].filter(Boolean).join(" · ")}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            <div className={grid ? "" : "mt-2.5"}>
              <SelectField
                value={body?.value}
                onChange={handleBody}
                placeholder={
                  tier?.value === "college"
                    ? "Pick a governing body…"
                    : "All leagues"
                }
              >
                {bodyOptions.map((b) => (
                  <option key={b.value} value={b.value} disabled={b.status === "coming"}>
                    {optionLabel(b)}
                  </option>
                ))}
              </SelectField>
            </div>
          </div>
        )}

        {/* Step 5 · League / Circuit */}
        {showSubDivisionStep && (
          <div className={sec}>
            <StepLabel step={5} title="League / Circuit" layout={layout} />
            {!grid && <Hint>Optional. Team search below always overrides.</Hint>}
            <div className={grid ? "" : "mt-2.5"}>
              <SelectField
                value={subDivision?.value}
                onChange={handleSubDivision}
                placeholder={`All ${body?.name || ""} divisions`}
              >
                {subDivisionOptions.map((s) => (
                  <option key={s.value} value={s.value} disabled={s.status === "coming"}>
                    {optionLabel(s)}
                  </option>
                ))}
              </SelectField>
            </div>
          </div>
        )}

        {/* Step 5b · Class / Division (State HS only) */}
        {subDivision?.classes?.length > 0 && (
          <div className={sec}>
            <StepLabel step={"5b"} title="Class / Division" layout={layout} />
            {!grid && (
              <Hint>Optional. Narrow {subDivision.name} to a class.</Hint>
            )}
            <div className={grid ? "" : "mt-2.5"}>
              <SelectField
                value={classDivision?.value}
                onChange={handleClassDivision}
                placeholder="All classes"
              >
                {f.classOptions.map((c) => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </SelectField>
            </div>
          </div>
        )}

        {/* Step 6 · Conference (NCAA only) */}
        {showConferenceStep && (
          <div className={sec}>
            <StepLabel step={6} title="Conference" layout={layout} />
            {!grid && <Hint>Optional. Skip to see every NCAA program.</Hint>}
            <div className={grid ? "" : "mt-2.5"}>
              <SelectField
                value={conference?.value}
                onChange={handleConference}
                placeholder="All conferences"
              >
                {NCAA_CONFERENCE_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.name}</option>
                    ))}
                  </optgroup>
                ))}
              </SelectField>
            </div>
          </div>
        )}

        {/* Step 7 · Team — a section in the sidebar; full-width row below
            the grid in the modal (rendered outside the grid there). */}
        {!grid && genderReady && tier && (
          <div className={sec}>
            <TeamStep
              layout={layout}
              anyLeague={body?.anyLeague === true}
              teamSearch={teamSearch}
              handleTeamSearch={handleTeamSearch}
              isLoadingTeams={isLoadingTeams}
              teamCountLabel={teamCountLabel}
              selectedTeam={selectedTeam}
              filteredTeams={filteredTeams}
              teams={teams}
              body={body}
              onTeamSelect={onTeamSelect}
            />
          </div>
        )}
      </div>

      {grid && genderReady && tier && (
        <div className="mt-5">
          <TeamStep
            layout={layout}
            anyLeague={body?.anyLeague === true}
            teamSearch={teamSearch}
            handleTeamSearch={handleTeamSearch}
            isLoadingTeams={isLoadingTeams}
            teamCountLabel={teamCountLabel}
            selectedTeam={selectedTeam}
            filteredTeams={filteredTeams}
            teams={teams}
            body={body}
            onTeamSelect={onTeamSelect}
          />
        </div>
      )}
    </div>
  );
};

// A CACHE partner team (one of ours, not a Cerebro program).
const PartnerMark = () => (
  <span title="CACHE Partner team" className="shrink-0">
    <Badge tone="soft">Partner</Badge>
  </span>
);

// Step 7 (search + team list). Sidebar rows are two lines (name+code, then
// conference below); modal rows keep the conference right-aligned. Either
// way the row's latest season is its own line ("Latest season 2025-26",
// the latest event on file as its title), never a year beside the name.
// A list the team feed did not fully answer (or a paused provider's saved
// copy) says so above the teams, in CACHE's words, never the server's error.
const TeamStep = ({
  layout,
  anyLeague = false,
  teamSearch,
  handleTeamSearch,
  isLoadingTeams,
  teamCountLabel,
  selectedTeam,
  filteredTeams,
  teams,
  body,
  onTeamSelect,
}) => {
  const grid = layout === "modal";
  const notice = isLoadingTeams ? "" : dataStatusNotice(teamsDataStatus(teams));
  return (
    <div>
      <StepLabel step={7} title="Team" layout={layout} />
      <div className={`relative ${grid ? "" : "mt-2"}`}>
        <svg
          className="pointer-events-none absolute left-[12px] top-1/2 h-4 w-4 -translate-y-1/2 text-bm-tertiary"
          fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
        >
          <circle cx="11" cy="11" r="7" />
          <path strokeLinecap="round" d="M20 20l-3.5-3.5" />
        </svg>
        <input
          type="text"
          value={teamSearch || ""}
          onChange={handleTeamSearch}
          placeholder="Search any team"
          className="bm-input !pl-9"
        />
      </div>
      <div className="flex items-center justify-between mt-2.5 gap-2 flex-wrap">
        <span className="bm-body-s text-bm-muted">
          {isLoadingTeams ? "Loading teams..." : anyLeague && (teamSearch || "").trim().length < 2 ? "Type at least two letters of the team name to search every league" : teamCountLabel}
        </span>
        {selectedTeam?.id && (
          <span className="inline-flex items-center gap-1.5 bm-label-xs text-bm-ink">
            <span className="w-2 h-2 rounded-full bg-bm-orange" />
            Selected: {teamDisplayName(selectedTeam)}
          </span>
        )}
      </div>
      {notice && (
        <NoticeState role="status" className="mt-2.5 bm-body-s !px-3 !py-2">
          {notice}
        </NoticeState>
      )}
      {!isLoadingTeams && filteredTeams.length > 0 && (
        <ul className={`mt-3 overflow-y-auto rounded-[8px] border border-bm-border bg-white divide-y divide-bm-mutedbg ${
          grid ? "max-h-[260px]" : "max-h-[420px]"
        }`}>
          {filteredTeams.slice(0, 100).map((t) => {
            const isSelected = selectedTeam?.id === t.id && (selectedTeam?.leagueKey || "") === (t.leagueKey || "");
            const isCachePartnerTeam = t.source === "cache_teams";
            // The name by the display rule (its age group, never a year
            // beside it); the season goes to the subline (D15).
            const name = teamDisplayName(t);
            const season = latestSeasonOf(t);
            const seasonTitle = latestEventTitle(t);
            const conferenceLabel = stripSeasonSuffix(String(t.conference || t.leagueName || t.location || "")).trim();
            // A same-named squad active the same season reads apart by its place.
            const twin = twinNoteOf(t);
            const twinNote = twin && twin.toLowerCase() !== conferenceLabel.toLowerCase() ? twin : "";
            return (
              <li
                key={`${t.id}|${t.leagueKey || t.leagueName || ""}`}
                onClick={() => onTeamSelect(t)}
                className={`relative px-4 py-2.5 cursor-pointer transition ${
                  isSelected ? "bg-bm-orange/5" : "hover:bg-bm-subtle"
                }`}
              >
                {isSelected && (
                  <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-bm-orange" />
                )}
                {grid ? (
                  <>
                    <div className="flex items-center justify-between gap-3 bm-body-m">
                      <span className="flex items-center gap-2 min-w-0">
                        {isCachePartnerTeam && <PartnerMark />}
                        <span className={`truncate ${isSelected ? "bm-h-xs text-bm-ink" : "text-bm-ink"}`}>
                          {name}
                          {!isCachePartnerTeam && t.alias && (
                            <span className="font-normal text-bm-muted"> ({t.alias})</span>
                          )}
                        </span>
                      </span>
                      {conferenceLabel && (
                        <span className="whitespace-nowrap bm-body-s text-bm-muted">
                          {conferenceLabel}
                        </span>
                      )}
                    </div>
                    {twinNote && <div className="mt-0.5 truncate bm-body-s text-bm-muted">{twinNote}</div>}
                    {season && (
                      <div title={seasonTitle} className="mt-0.5 bm-body-s text-bm-tertiary">
                        Latest season {season}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex min-w-0 items-center gap-2 bm-body-m">
                      {isCachePartnerTeam && <PartnerMark />}
                      <span className="truncate bm-h-xs text-bm-ink">
                        {name}
                        {!isCachePartnerTeam && t.alias && (
                          <span className="font-normal text-bm-muted"> ({t.alias})</span>
                        )}
                      </span>
                    </div>
                    {conferenceLabel && (
                      <div className="mt-0.5 truncate bm-body-s text-bm-muted">
                        {conferenceLabel}
                      </div>
                    )}
                    {twinNote && <div className="mt-0.5 truncate bm-body-s text-bm-muted">{twinNote}</div>}
                    {season && (
                      <div title={seasonTitle} className="mt-0.5 bm-body-s text-bm-tertiary">
                        Latest season {season}
                      </div>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!isLoadingTeams && teams.length === 0 && !(anyLeague && (teamSearch || "").trim().length < 2) && (
        <p className="mt-3 rounded-[8px] border border-bm-border bg-bm-subtle p-3 bm-body-s text-bm-muted">
          No teams found for this selection.{" "}
          {body
            ? "Try clearing the league filter, or pick a different one."
            : "Try narrowing by region or league."}
        </p>
      )}
    </div>
  );
};

export default CascadePicker;
